import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { fmtDateTime } from '../../lib/dates';
import { notifyBriefOwnersOfQuestion } from '../../lib/briefQuestionNotify';
import QACard from './QACard';

// Supplier-facing "Questions and clarifications" section (supplier-questions
// brief §4/§5/§9 + the tidy-up tweak brief). Sits between the brief body and the
// respond card so a published answer — effectively an amendment to the brief —
// is seen before the accept/decline decision, not below it.
//
// Order within the section: Shared with everyone quoting (published, ANONYMISED
// — outranks the supplier's own thread) → Your questions → Ask box. The whole
// thing is one white card to match the other sections on the page.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const timeOf = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
const dateOf = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;

// Answer timestamp within a thread: relative if recent, else time-only when it's
// the same calendar day as the question (the date isn't repeated), else full.
const answerStamp = (answerIso, questionIso) => {
  const d = new Date(answerIso);
  if (isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  if (ms >= 0 && ms < 86400000) {
    const h = Math.floor(ms / 3600000);
    if (h >= 1) return `${h} hour${h === 1 ? '' : 's'} ago`;
    const m = Math.max(1, Math.floor(ms / 60000));
    return `${m} min${m === 1 ? '' : 's'} ago`;
  }
  const q = questionIso ? new Date(questionIso) : null;
  const sameDay = q && !isNaN(q.getTime()) && d.toDateString() === q.toDateString();
  return sameDay ? timeOf(d) : `${dateOf(d)}, ${timeOf(d)}`;
};

const EYEBROW = "font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-forest";
// Quieter than the eyebrows (tweak §7) so it doesn't compete with the section
// headings inside a card.
const ANSWER_LABEL = "font-['Metropolis'] text-[12px] font-semibold text-fusion-mid-grey";

export default function BriefQuestions({
  briefId,
  supplierId,
  supplierUserId,
  questionsClose,   // ISO string | null
  responseDeadline, // effective response deadline — once it passes, questions close too
  briefClosed,      // brief awarded/closed/cancelled — no new questions (§9)
  submittedAt,      // ISO string | null — when this supplier submitted, for §5
  onNewCount,       // (count) => void — reports clarifications published since submit (§5)
  briefName,        // for the owner-notification email
  supplierName,     // for the owner-notification email
}) {
  const [published, setPublished] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  const [justAsked, setJustAsked] = useState(false);

  // The ask box shuts when the question deadline passes, OR the RESPONSE deadline
  // passes (a legacy brief may have no questions_close — you still can't ask
  // about a brief whose response window is long gone), OR the brief is
  // awarded/closed/cancelled (§9). Existing threads stay readable + answerable.
  const questionsDeadlinePassed = !!questionsClose && Date.now() > new Date(questionsClose).getTime();
  const responseDeadlinePassed = !!responseDeadline && Date.now() > new Date(responseDeadline).getTime();
  const closed = briefClosed || questionsDeadlinePassed || responseDeadlinePassed;

  const load = useCallback(async () => {
    if (!briefId || !supplierId) return;
    setLoading(true);
    // Published Q&A comes from the anonymised view — no supplier_id / user ids
    // are exposed here, so a supplier can never learn who asked what.
    const [pubRes, mineRes] = await Promise.all([
      supabase
        .from('brief_published_questions')
        .select('question_id, question_body, answer_body, answer_attachment_url, answer_attachment_name, published_at, answered_at')
        .eq('brief_id', briefId)
        .order('published_at', { ascending: true }),
      supabase
        .from('brief_questions')
        .select('id, body, status, visibility, created_at, asked_by_user_id, asker:supplier_users!asked_by_user_id(full_name), brief_question_answers(id, body, created_at, answered_by_user_id, attachment_url, attachment_name, answerer:internal_users!answered_by_user_id(full_name))')
        .eq('brief_id', briefId)
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: true }),
    ]);
    setPublished(pubRes.data || []);
    setMine(mineRes.data || []);
    setLoading(false);
  }, [briefId, supplierId]);

  useEffect(() => { load(); }, [load]);

  // §5 — report how many clarifications were published after the supplier
  // submitted, so the page can raise the top-of-page amber alert.
  useEffect(() => {
    if (!onNewCount) return;
    const ts = submittedAt ? new Date(submittedAt).getTime() : null;
    if (ts == null) { onNewCount(0); return; }
    onNewCount(published.filter((q) => q.published_at && new Date(q.published_at).getTime() > ts).length);
  }, [published, submittedAt, onNewCount]);

  const ask = async () => {
    const trimmed = body.trim();
    if (!trimmed || closed) return;
    setAsking(true);
    setError(null);
    const { error: insErr } = await supabase.from('brief_questions').insert([{
      brief_id: briefId,
      supplier_id: supplierId,
      asked_by_user_id: supplierUserId || null,
      body: trimmed,
    }]);
    setAsking(false);
    if (insErr) { setError("Couldn't send your question — please try again."); return; }
    setBody('');
    setJustAsked(true);
    setTimeout(() => setJustAsked(false), 6000);
    // Email the brief owners — an unanswered question has a growing cost, so
    // each one notifies immediately (§7). Best-effort; never blocks the ask.
    notifyBriefOwnersOfQuestion({ briefId, briefName, supplierName, questionBody: trimmed }).catch(() => {});
    load();
  };

  const submittedTs = submittedAt ? new Date(submittedAt).getTime() : null;
  const isNewSinceSubmit = (iso) => submittedTs != null && iso && new Date(iso).getTime() > submittedTs;
  const hasShared = published.length > 0;

  return (
    <section id="brief-questions" aria-labelledby="brief-questions-heading" className="mb-8">
      {/* One white card to match the brief body / respond / Response Submitted
          cards around it (tweak §5). */}
      <div className="bg-white border border-line rounded-xl p-6">
        <h2 id="brief-questions-heading" className="font-['Metropolis'] text-lg font-bold text-ink mb-1">Questions and clarifications</h2>
        <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-5">
          Not sure about something? Ask before you price.{hasShared ? ' Answers shared with everyone quoting appear here — read them first.' : ''}
        </p>

        {loading ? (
          <p className="font-['Metropolis'] text-sm text-[#51534A] italic">Loading questions…</p>
        ) : (
          <div className="space-y-6">
            {/* 1 — Shared with everyone quoting (published, anonymised). Shown
                only when there ARE any (tweak §2). */}
            {hasShared && (
              <div>
                <div className={`${EYEBROW} mb-2`}>Shared with everyone quoting</div>
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey mb-3">These clarifications apply to all suppliers — read them before pricing.</p>
                <div className="space-y-3">
                  {published.map((q) => {
                    const isNew = isNewSinceSubmit(q.published_at);
                    return (
                      <div key={q.question_id} className="border border-line rounded-xl bg-white overflow-hidden">
                        <div className="px-4 py-3">
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-['Metropolis'] text-sm text-ink font-semibold">{q.question_body}</p>
                            {isNew && (
                              <span className="flex-shrink-0 font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] rounded-md px-2 py-0.5 bg-[#E89A2E]/15 text-[#7A4E10] border border-[#E89A2E]/40 whitespace-nowrap">
                                New · after you submitted
                              </span>
                            )}
                          </div>
                          {q.answer_body && (
                            <div className="mt-2 pt-2 border-t border-line">
                              <div className={`${ANSWER_LABEL} mb-1`}>Answer from Fusion 2K · {answerStamp(q.answered_at, q.asked_at)}</div>
                              <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{q.answer_body}</p>
                              {q.answer_attachment_url && (
                                <a href={q.answer_attachment_url} target="_blank" rel="noopener noreferrer"
                                  className="mt-2 inline-flex items-center gap-1.5 font-['Metropolis'] text-[12px] font-semibold text-fusion-forest hover:underline">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
                                  {q.answer_attachment_name || 'View attachment'}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 2 — Your questions (shared QACard — one component both sides use) */}
            {mine.length > 0 && (
              <div>
                <div className={`${EYEBROW} mb-2`}>Your questions</div>
                <div className="space-y-3">
                  {mine.map((q) => (
                    <QACard
                      key={q.id}
                      side="supplier"
                      question={{
                        id: q.id, body: q.body, status: q.status, visibility: q.visibility, createdAt: q.created_at,
                        askedByMe: !!(q.asked_by_user_id && q.asked_by_user_id === supplierUserId),
                        askerName: q.asker?.full_name, askerOrg: supplierName,
                        answers: (q.brief_question_answers || []).map((a) => ({ id: a.id, body: a.body, createdAt: a.created_at, answererName: a.answerer?.full_name, attachmentUrl: a.attachment_url, attachmentName: a.attachment_name })),
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 3 — Ask box */}
            <div>
              <div className={`${EYEBROW} mb-2`}>Ask a question</div>
              {/* Privacy note ABOVE the input (tweak §4) — someone weighing up a
                  commercially sensitive question needs it before they type. */}
              <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-2">
                Only Fusion 2K sees your question. If they choose to share the answer with everyone quoting, it's shown anonymously — your name is never attached.
              </p>
              {justAsked && (
                <p role="status" className="font-['Metropolis'] text-sm text-fusion-forest mb-2">Sent — Fusion 2K will get back to you here.</p>
              )}
              <label htmlFor="brief-question-input" className="sr-only">Your question about this brief</label>
              <textarea
                id="brief-question-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={closed || asking}
                rows={3}
                aria-describedby={closed ? 'brief-questions-closed-reason' : 'brief-questions-close-line'}
                placeholder={closed ? 'Questions are closed for this brief.' : 'e.g. Does the plinth need to take vehicle weight?'}
                className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors disabled:bg-[#F1F3EA] disabled:text-[#9BA08C] disabled:cursor-not-allowed" />
              {closed ? (
                <p id="brief-questions-closed-reason" className="font-['Metropolis'] text-[12px] text-[#7A4E10] mt-1">
                  {briefClosed
                    ? 'This brief has closed, so new questions aren’t being taken. Your existing threads stay here for your records.'
                    : questionsDeadlinePassed
                      ? `Questions closed on ${fmtDateTime(questionsClose)}. Existing threads can still be answered.`
                      : 'The response deadline has passed, so questions are now closed. Existing threads can still be answered.'}
                </p>
              ) : (
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={ask}
                    disabled={!body.trim() || asking}
                    className="rounded-xl font-['Metropolis'] text-xs font-bold px-4 py-2 transition-colors bg-[#92D400] text-[#131313] hover:bg-[#a4e810] disabled:bg-[#E6EADB] disabled:text-[#9BA08C] disabled:cursor-not-allowed">
                    {asking ? 'Sending…' : 'Send question'}
                  </button>
                  {/* §3 — tell them when questions close, beside the box. */}
                  {questionsClose && (
                    <p id="brief-questions-close-line" className="font-['Metropolis'] text-[11px] text-[#51534A]">
                      Questions close {fmtDateTime(questionsClose)}.
                    </p>
                  )}
                </div>
              )}
              {error && <p role="alert" className="font-['Metropolis'] text-[12px] text-[#E0290A] mt-1">{error}</p>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
